import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { FreightModel } from 'src/app/models/management/freight.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CityTableModel } from 'src/app/models/tables/management/city-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CityService } from 'src/app/services/management/city.service';
import { FreightService } from 'src/app/services/management/freight.service';
import { SectorService } from 'src/app/services/management/sector.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-freight',
  templateUrl: './register-freight.component.html',
  styleUrls: ['./register-freight.component.scss'],
  standalone: false
})
export class RegisterFreightComponent implements OnInit {

  @ViewChild('selectCityOrigin') cityOriginSelectionModalComponent?: CustomModalComponent | null = null;
  @ViewChild('selectCityDestiny') cityDestinySelectionModalComponent?: CustomModalComponent | null = null;

  ascendantCity: boolean = true;
  cityData: PagedResponseModel<CityTableModel> = new PagedResponseModel<CityTableModel>();
  cityDestiny: string = '';
  cityOrigin: string = '';
  columnSortCity: string | null = null;
  componentCitySelection = CustomTableSelectComponent;
  departmentDestiny: string = '';
  departmentOrigin: string = '';
  disabledForm: boolean = false;
  filtersCity: ColumnResponseModel[] = [];
  formFreight: FormGroup = new FormGroup({});
  freightContract: FreightModel = new FreightModel();
  freightId: number = 0;
  headersCity: ColumnResponseModel[] = [];
  inputsForSelectCityModal$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    filters: [],
    showCheckbox: true,
    showRecover: true
  });
  isOpened: string | undefined = undefined;
  limitCity: number = 10;
  listConcepts: any[] = [];
  listSectors: any[] = [];
  nameWindow: string = 'Captura Fletes';
  outputCity$ = new Subject<any>();
  pageCity: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  subscription: Subscription;
  url: string = 'freight';

  constructor(private confirmDialogService: ConfirmDialogService,
    private activatedRoute: ActivatedRoute,
    private cityService: CityService,
    private formBuilder: FormBuilder,
    private freightService: FreightService,
    private headerService: HeaderService,
    private location: Location,
    private sectorService: SectorService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) {
    this.validateForm(new FreightModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.freightId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
    this.subscription = this.outputCity$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeCity(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeCity(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeCity(selectedItem: any, type: 'origin' | 'destiny') {
    const [data] = selectedItem;
    this.updateCityData(data, type);

    if (type === 'destiny') {
      await this.getAllLists('sectors');
    }
  }

  async changeConcept(conceptId: number) {
    this.freightContract.conceptId = conceptId;
    const { cityOriginId, cityDestinyId } = this.freightContract;

    if (!cityOriginId || !cityDestinyId || !conceptId) {
      return;
    }
    await this.getFreightByKeys();
  }

  changeSector(sectorId: number | null) {
    const {
      freightId,
      cityOriginId,
      cityDestinyId,
      departmentOriginId,
      departmentDestinyId
    } = this.freightContract;

    this.freightContract = {
      ...new FreightModel(),
      freightId,
      sectorId,
      cityOriginId,
      cityDestinyId,
      departmentOriginId,
      departmentDestinyId
    };

    this.validateForm(this.freightContract);
  }

  async createFreight(modelFreight: FreightModel) {
    try {
      const response = await firstValueFrom(this.freightService.createFreight(modelFreight));
      this.freightId = response.freightId;
      this.freightContract = response;
      this.validateForm(this.freightContract);
      this.location.replaceState(`/${this.url}/register/${this.freightId}`);
      this.toastHandlerService.showToast('success', `El flete fue creado exitosamente.`);
    } catch (error) {
      console.error('Error al crear el flete.', error);
    }
  }

  async createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear fletes.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.freightId = 0;
    this.cityOrigin = '';
    this.cityDestiny = '';
    this.departmentDestiny = '';
    this.departmentOrigin = '';
    this.freightContract = new FreightModel();
    this.validateForm(this.freightContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async clearCity(type: 'origin' | 'destiny') {
    this.freightId = 0;
    this.listSectors = [];
    const modelFreight = new FreightModel();

    if (type === 'origin') {
      this.cityOrigin = '';
      this.departmentOrigin = '';
      this.cityDestiny = '';
      this.departmentDestiny = '';
      this.location.replaceState(`/${this.url}/register`);
    }

    if (type === 'destiny') {
      this.cityDestiny = '';
      this.departmentDestiny = '';
      const { freightId, cityOriginId, departmentOriginId } = this.freightContract;
      modelFreight.freightId = freightId;
      modelFreight.cityOriginId = cityOriginId;
      modelFreight.departmentOriginId = departmentOriginId;
    }

    this.freightContract = modelFreight;
    this.validateForm(this.freightContract);
  }

  async deleteFreight() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para eliminar fletes.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (!this.freightId) {
      this.toastHandlerService.showToast('warning', 'Debe crear un flete.');
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el flete?')) {
      try {
        await firstValueFrom(this.freightService.deleteFreight(this.freightId));
        this.createNewRegister();
        this.toastHandlerService.showToast('success', 'El flete fue eliminado exitosamente.');
      } catch (error) {
        console.error('Error al eliminar el flete.', error);
      }
    }
  }

  async getAllLists(...listsToLoad: Array<'sectors' | 'concepts'>) {
    try {
      const lists = {
        sectors: async () => {
          const cityDestinyId = this.freightContract.cityDestinyId;
          this.listSectors = await firstValueFrom(this.sectorService.getAllSectorsForSelect(cityDestinyId));
        },
        concepts: async () => {
          this.listConcepts = await firstValueFrom(this.freightService.getAllConceptsForSelect());
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.sectors(),
          lists.concepts()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getCitiesForSelection() {
    try {
      const response = await firstValueFrom(this.cityService.getCitiesForSelection(this.pageCity, this.limitCity,
        this.columnSortCity, this.ascendantCity, this.filtersCity));
      this.cityData = response;
      this.updateInputsForSelectCityModal();
    } catch (error) {
      console.error('Error al obtener las ciudades para el modal.', error);
    }
  }

  async getColumnsForCitiesSelection() {
    try {
      if (this.headersCity.length === 0) {
        const response = await firstValueFrom(this.cityService.getColumnsForCitySelection());
        this.headersCity = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para el modal de ciudades.', error);
    }
  }

  async getDestinityCity(cityId: number) {
    try {
      const response: any = await firstValueFrom(this.cityService.getCityDepartment(cityId));
      this.cityDestiny = response.city || '';
      this.departmentDestiny = response.department || '';
    } catch (error) {
      console.error('Error al obtener la ciudad de destino.', error);
    }
  }

  async getFreightById() {
    try {
      const response = await firstValueFrom(this.freightService.getFreightById(this.freightId));
      this.freightContract = response;
      await this.loadFreightFormData(response);
      this.validateForm(this.freightContract);
    } catch (error) {
      console.error('Error al obtener el flete por id.', error);
    }
  }

  async getFreightByKeys() {
    try {
      const { cityOriginId, cityDestinyId, sectorId, conceptId, constructionSiteId } = this.freightContract;
      const response = await firstValueFrom(this.freightService.getFreightByKeys(cityOriginId, cityDestinyId,
        sectorId, conceptId, constructionSiteId));
      this.freightId = response.freightId;
      this.freightContract = response;
      this.validateForm(this.freightContract);
    } catch (error) {
      console.error('Error al consultar el flete por claves.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists('concepts'),
      this.getPermissionProfileByUrl(),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getOriginCity(cityId: number) {
    try {
      const response: any = await firstValueFrom(this.cityService.getCityDepartment(cityId));
      this.cityOrigin = response.city || '';
      this.departmentOrigin = response.department || '';
    } catch (error) {
      console.error('Error al obtener la ciudad de origen.', error);
    }
  }

  async loadFreightFormData(freight: FreightModel) {
    await Promise.all([
      this.getAllLists(),
      this.getOriginCity(freight.cityOriginId),
      this.getDestinityCity(freight.cityDestinyId),
    ]);
  }

  async updateCityData(data: any, type: 'origin' | 'destiny') {
    const { code, name, departmentCode, departmentName, cityId, departmentId } = data;
    const modelFreight = new FreightModel();

    if (type === 'origin') {
      this.freightId = 0;
      this.cityDestiny = '';
      this.departmentDestiny = '';
      this.cityOrigin = `${code} - ${name}`;
      this.departmentOrigin = `${departmentCode} - ${departmentName}`;
      modelFreight.cityOriginId = cityId;
      modelFreight.departmentOriginId = departmentId;
      this.location.replaceState(`/${this.url}/register`);
    }

    if (type === 'destiny') {
      this.cityDestiny = `${code} - ${name}`;
      this.departmentDestiny = `${departmentCode} - ${departmentName}`;
      const { cityOriginId, departmentOriginId } = this.freightContract;
      modelFreight.cityOriginId = cityOriginId;
      modelFreight.departmentOriginId = departmentOriginId;
      modelFreight.cityDestinyId = cityId;
      modelFreight.departmentDestinyId = departmentId;
    }

    this.freightContract = modelFreight;
    this.validateForm(this.freightContract);
  }

  async handleInitialState() {
    if (this.freightId) {
      await this.getFreightById();
    } else {
      this.createNewRegister();
    }
  }

  async openCitySelectionModal(type: 'origin' | 'destiny') {
    this.filtersCity = [];
    await Promise.all([
      this.getCitiesForSelection(),
      this.getColumnsForCitiesSelection(),
    ]);

    this.updateInputsForSelectCityModal();
    if (type === 'origin') {
      this.cityOriginSelectionModalComponent?.show();
    }

    if (type === 'destiny') {
      this.cityDestinySelectionModalComponent?.show();
    }
  }

  async onFilterChangeCity(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersCity = this.removeDuplicates(filters);
    this.filtersCity.forEach((filter: any) => this.processFilterCity(filter));
    this.updateInputsForSelectCityModal();
  }

  async onPageChangeCity(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageCity = event.page;
    this.limitCity = event.pageSize;
    this.columnSortCity = event.sortField || null;
    this.ascendantCity = event.sortOrder === 1;
    await this.getCitiesForSelection();
  }

  processFilterCity(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersCity = this.filtersCity.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersCity.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async send() {
    this.validateFormControls();
    if (!this.formFreight.valid) {
      this.showValidationMessages();
      return;
    }
    const modelFreight = this.formFreight.getRawValue() as FreightModel;
    if (this.freightId == 0) {
      await this.createFreight(modelFreight);
    } else {
      await this.updateFreight(modelFreight);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {

      departmentOriginId: {
        required: 'El Departamento de Origen es obligatorio.',
        min: 'Debe seleccionar un Departamento de Origen válido.'
      },
      cityOriginId: {
        required: 'La Ciudad de Origen es obligatoria.',
        min: 'Debe seleccionar una Ciudad de Origen válida.'
      },
      departmentDestinyId: {
        required: 'El Departamento de Destino es obligatorio.',
        min: 'Debe seleccionar un Departamento de Destino válido.'
      },
      cityDestinyId: {
        required: 'La Ciudad de Destino es obligatoria.',
        min: 'Debe seleccionar una Ciudad de Destino válida.'
      },
      conceptId: {
        required: 'El Concepto es obligatorio.',
        min: 'Debe seleccionar un Concepto válido.'
      },
      value: {
        min: 'El Valor no puede ser negativo.',
        max: 'El Valor no puede superar 99.999.999.999,99.'
      },
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formFreight.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    setTimeout(() => {
      if (this.permissionUpdatable) {
        this.disabledForm = false;
        this.formFreight.enable();
      } else {
        this.disabledForm = true;
        this.formFreight.disable();
      }
    });
  }

  async updateFreight(modelFreight: FreightModel) {
    this.freightContract.value = modelFreight.value;
    this.freightContract.sectorId = modelFreight.sectorId;
    this.freightContract.conceptId = modelFreight.conceptId;
    try {
      const response = await firstValueFrom(this.freightService.updateFreight(this.freightContract));
      this.freightContract = response;
      this.validateForm(this.freightContract);
      this.toastHandlerService.showToast('success', `El flete fue modificado exitosamente.`);
    } catch (error) {
      console.error('Error al actualizar el flete.', error);
    }
  }

  updateInputsForSelectCityModal() {
    this.inputsForSelectCityModal$.next({
      headers: this.headersCity,
      data: this.cityData,
      filters: this.filtersCity,
      showCheckbox: true,
      showRecover: true
    });
  }

  validateForm(freight: FreightModel) {
    this.formFreight = this.formBuilder.group({
      sectorId: [freight.sectorId],
      departmentOriginId: [freight.departmentOriginId, [Validators.required, Validators.min(1)]],
      cityOriginId: [freight.cityOriginId, [Validators.required, Validators.min(1)]],
      departmentDestinyId: [freight.departmentDestinyId, [Validators.required, Validators.min(1)]],
      cityDestinyId: [freight.cityDestinyId, [Validators.required, Validators.min(1)]],
      conceptId: [freight.conceptId, [Validators.required, Validators.min(1)]],
      value: [freight.value, [Validators.min(0), Validators.max(99999999999.99)]],
    });
    this.updateFormPermision();
  }

  validateFormControls() {
    Object.keys(this.formFreight.controls).forEach((key) => {
      const control = this.formFreight.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
